import { useEffect, useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { Navigate, useNavigate } from 'react-router-dom'
import { BrandLogo } from '../components/BrandLogo'
import { supabaseConfigurado } from '../lib/supabase'
import { useAuth } from '../state/AuthContext'
import { useAppTheme } from '../state/ThemeContext'
import { THEME_OPTIONS, type ThemeId } from '../theme'

const POST_LOGIN_PATH = '/'

export function LoginPage() {
  const { login, isAuthenticated, bootstrapping, loading, error, clearError } = useAuth()
  const { themeId, setThemeId } = useAppTheme()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; senha?: string }>({})

  useEffect(() => {
    clearError()
  }, [clearError])

  if (bootstrapping) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (isAuthenticated) {
    return <Navigate to={POST_LOGIN_PATH} replace />
  }

  function validate() {
    const next: { email?: string; senha?: string } = {}
    if (!email.trim()) next.email = 'Email é obrigatório'
    else if (!/\S+@\S+\.\S+/.test(email)) next.email = 'Email inválido'
    if (!senha) next.senha = 'Senha é obrigatória'
    setFieldErrors(next)
    return Object.keys(next).length === 0
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    try {
      await login(email, senha)
      navigate(POST_LOGIN_PATH, { replace: true })
    } catch {
      /* erro já está no contexto */
    }
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <TextField
            select
            size="small"
            label="Tema"
            value={themeId}
            onChange={(e) => setThemeId(e.target.value as ThemeId)}
            sx={{ minWidth: 140 }}
          >
            {THEME_OPTIONS.map((opt) => (
              <MenuItem key={opt.id} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 400,
          }}
        >
          <BrandLogo size={32} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Entre para acessar os painéis de marketing
          </Typography>

          {!supabaseConfigurado ? (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Supabase não configurado. Copie <code>web/.env.example</code> para{' '}
              <code>web/.env.local</code> e preencha a URL e a publishable key do projeto.
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (fieldErrors.email) setFieldErrors((f) => ({ ...f, email: undefined }))
              }}
              error={Boolean(fieldErrors.email)}
              helperText={fieldErrors.email}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="senha"
              label="Senha"
              type="password"
              id="senha"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => {
                setSenha(e.target.value)
                if (fieldErrors.senha) setFieldErrors((f) => ({ ...f, senha: undefined }))
              }}
              error={Boolean(fieldErrors.senha)}
              helperText={fieldErrors.senha}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || !supabaseConfigurado}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Entrar'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  )
}
